/**
 * Functional Design Document (FDD) generation from tender attachments.
 *
 * Takes the aggregated extracted text from all tender documents and asks
 * the LLM to produce a complete bilingual FDD with the standard 7 sections.
 * Returns a structured object keyed by section so it can be persisted
 * column-by-column and re-rendered into DOCX / PDF.
 */

import { getClient } from "./openai.js";

export const FDD_SECTIONS = [
  { key: "overview",                  arLabel: "نظرة عامة",                  enLabel: "Overview" },
  { key: "functionalRequirements",    arLabel: "المتطلبات الوظيفية",         enLabel: "Functional Requirements" },
  { key: "nonFunctionalRequirements", arLabel: "المتطلبات غير الوظيفية",     enLabel: "Non-Functional Requirements" },
  { key: "dataRequirements",          arLabel: "متطلبات البيانات",           enLabel: "Data Requirements" },
  { key: "interfaces",                arLabel: "الواجهات والتكاملات",        enLabel: "Interfaces & Integrations" },
  { key: "constraints",               arLabel: "القيود والافتراضات",         enLabel: "Constraints & Assumptions" },
  { key: "acceptanceCriteria",        arLabel: "معايير القبول",              enLabel: "Acceptance Criteria" },
] as const;

export type FddSectionKey = typeof FDD_SECTIONS[number]["key"];

export interface FddContent {
  overview: string;
  functionalRequirements: string;
  nonFunctionalRequirements: string;
  dataRequirements: string;
  interfaces: string;
  constraints: string;
  assumptions: string;
  acceptanceCriteria: string;
}

const EMPTY_FDD: FddContent = {
  overview: "", functionalRequirements: "", nonFunctionalRequirements: "",
  dataRequirements: "", interfaces: "", constraints: "", assumptions: "",
  acceptanceCriteria: "",
};

/**
 * Generate a complete FDD from concatenated tender document text.
 * Uses JSON-mode for deterministic parsing.
 */
export async function generateFddFromDocuments(args: {
  language: "ar" | "en";
  tenderTitle: string;
  documentsText: string;
  tenderSummary?: string | null;
}): Promise<FddContent> {
  const { client, model } = await getClient();
  const { language, tenderTitle, documentsText, tenderSummary } = args;
  const isAr = language === "ar";

  // Truncate to stay within context — keep both ends of the docs.
  const MAX = 60_000;
  const docs = documentsText.length > MAX
    ? documentsText.slice(0, MAX / 2) + "\n\n[...]\n\n" + documentsText.slice(-MAX / 2)
    : documentsText;

  const sectionsList = isAr
    ? `- overview: نظرة عامة (الغرض، السياق، نطاق المشروع)
- functionalRequirements: المتطلبات الوظيفية (قائمة مفصّلة بالميزات الوظيفية كنقاط FR-1, FR-2 ...)
- nonFunctionalRequirements: المتطلبات غير الوظيفية (الأداء، الأمان، التوفر، قابلية التوسع — كنقاط NFR-1 ...)
- dataRequirements: متطلبات البيانات (الكيانات، حقول البيانات، الاحتفاظ، السرية)
- interfaces: الواجهات والتكاملات (واجهات المستخدم، واجهات API، الأنظمة الخارجية)
- constraints: القيود (التقنية، التنظيمية، الزمنية، الميزانية)
- assumptions: الافتراضات (ما يُفترض أنه متاح/معلوم خلال التنفيذ)
- acceptanceCriteria: معايير القبول (شروط قبول واضحة وقابلة للاختبار — كنقاط AC-1 ...)`
    : `- overview: Overview (purpose, context, scope)
- functionalRequirements: Functional Requirements (detailed bullet list as FR-1, FR-2 …)
- nonFunctionalRequirements: Non-Functional Requirements (performance, security, availability, scalability as NFR-1 …)
- dataRequirements: Data Requirements (entities, fields, retention, confidentiality)
- interfaces: Interfaces & Integrations (UI, APIs, external systems)
- constraints: Constraints (technical, regulatory, schedule, budget)
- assumptions: Assumptions (what is assumed available/known during delivery)
- acceptanceCriteria: Acceptance Criteria (clear, testable bullet list as AC-1 …)`;

  const system = isAr
    ? "أنت محلل أنظمة سعودي خبير في إعداد وثائق التصميم الوظيفي (FDD) للمشاريع الحكومية. اعتمد فقط على النص المُرفق من المناقصة، ولا تخترع متطلبات غير موجودة. صِغ النص رسمياً وواضحاً، واستخدم تنسيق Markdown بسيط (عناوين فرعية ## ونقاط -). أعِد الناتج بصيغة JSON فقط."
    : "You are an expert Saudi systems analyst preparing Functional Design Documents (FDDs) for government projects. Rely strictly on the supplied tender text — do not invent requirements that aren't there. Use formal, clear prose with simple Markdown (## sub-headings, - bullets). Return JSON only.";

  const user = isAr
    ? `وثيقة تصميم وظيفي كاملة للمناقصة التالية. أعد JSON يحتوي الحقول التالية فقط، وكل حقل بنص Markdown باللغة العربية:

${sectionsList}

عنوان المناقصة: ${tenderTitle}
${tenderSummary ? `\nملخص:\n${tenderSummary.slice(0, 4000)}\n` : ""}
المحتوى المُستخرج من مرفقات المناقصة:
"""
${docs}
"""`
    : `Produce a complete FDD for the tender below. Return JSON containing exactly these fields, each as Markdown English text:

${sectionsList}

Tender title: ${tenderTitle}
${tenderSummary ? `\nSummary:\n${tenderSummary.slice(0, 4000)}\n` : ""}
Content extracted from tender attachments:
"""
${docs}
"""`;

  const resp = await client.chat.completions.create({
    model,
    response_format: { type: "json_object" },
    messages: [
      { role: "system", content: system },
      { role: "user", content: user },
    ],
    temperature: 0.2,
  });

  const raw = resp.choices[0]?.message?.content ?? "{}";
  let parsed: any = {};
  try { parsed = JSON.parse(raw); } catch { parsed = {}; }
  const str = (v: any) => (typeof v === "string" ? v : "");
  return {
    ...EMPTY_FDD,
    overview: str(parsed.overview),
    functionalRequirements: str(parsed.functionalRequirements),
    nonFunctionalRequirements: str(parsed.nonFunctionalRequirements),
    dataRequirements: str(parsed.dataRequirements),
    interfaces: str(parsed.interfaces),
    constraints: str(parsed.constraints),
    assumptions: str(parsed.assumptions),
    acceptanceCriteria: str(parsed.acceptanceCriteria),
  };
}

/**
 * Merge constraints + assumptions into one "Constraints & Assumptions"
 * field for display, matching FDD_SECTIONS schema.
 */
export function fddForExport(row: any): Record<string, string> {
  const constraints = row?.constraints ?? "";
  const assumptions = row?.assumptions ?? "";
  const merged = [constraints, assumptions].filter(Boolean).join("\n\n");
  return {
    overview: row?.overview ?? "",
    functionalRequirements: row?.functionalRequirements ?? "",
    nonFunctionalRequirements: row?.nonFunctionalRequirements ?? "",
    dataRequirements: row?.dataRequirements ?? "",
    interfaces: row?.interfaces ?? "",
    constraints: merged,
    acceptanceCriteria: row?.acceptanceCriteria ?? "",
  };
}
